import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormHelperText,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useApi } from "../../hooks/useApi";
import { useLatest } from "../../hooks/useLatest";
import { StorefrontSummary } from "../../types/evidence";
import { DOMAIN_MESSAGE, domainMessage, requiredPolicySlots } from "../../utils/storefrontFields";
import StorefrontEditor from "./StorefrontEditor";

// A deleted store leaves origin 'shopify' on a row nothing backs any more, so the link decides.
const originLabel = (row: StorefrontSummary) => {
  if (row.shopify_account_id !== null) return "Shopify";
  return row.origin === "order" ? "Seen in orders" : "Added manually";
};

// The same four dots the table shows: any one of them missing is a row an operator still has to fill.
const needsSetup = (row: StorefrontSummary) =>
  !row.completeness.logo
  || !row.completeness.checkout
  || row.completeness.policies < requiredPolicySlots(row).length
  || !row.completeness.contactEmail;

// Rows an operator still has to fill come first; the rest read alphabetically.
function sortRows(rows: StorefrontSummary[]): StorefrontSummary[] {
  return [...rows].sort((a, b) => {
    if (needsSetup(a) !== needsSetup(b)) return needsSetup(a) ? -1 : 1;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
}

function StatusDot({ done, label }: { done: boolean; label: string }) {
  return (
    <Tooltip title={label}>
      <Box
        role="img"
        aria-label={label}
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: done ? "success.main" : "action.disabled",
        }}
      />
    </Tooltip>
  );
}

export default function StorefrontsPanel({ companyId, onSaved, openStorefrontId = null }: {
  companyId: number;
  onSaved?: (message: string) => void;
  /** A row to open as soon as the list loads: a dispute's attention item links straight to its storefront. */
  openStorefrontId?: number | null;
}) {
  const { get, post } = useApi();

  const [rows, setRows] = useState<StorefrontSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ domain: "", name: "" });
  const [addError, setAddError] = useState<string | null>(null);
  // A 400 the API answers about the domain reads under the field, not with the other failures.
  const [addDomainError, setAddDomainError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The company id is part of the request path, so a response for the company shown before a
  // switch must not land on the one shown now.
  const companyIdRef = useLatest(companyId);
  const load = useCallback(() => {
    const requested = companyId;
    setLoadError(null);
    get<{ storefronts: StorefrontSummary[] }>(`/api/companies/${requested}/storefronts`)
      .then(({ storefronts }) => {
        if (companyIdRef.current !== requested) return;
        setRows(sortRows(storefronts));
        if (openStorefrontId !== null && storefronts.some((row) => row.storefront_id === openStorefrontId)) setOpenId(openStorefrontId);
      })
      .catch((err: any) => {
        if (companyIdRef.current !== requested) return;
        setRows([]);
        setLoadError(err?.message || "Failed to load the storefronts");
      });
  }, [get, companyId, companyIdRef, openStorefrontId]);

  // Dropped on switch: keeping them would show the previous company's storefronts, editor open.
  useEffect(() => {
    setRows(null);
    setOpenId(null);
    setAdding(false);
    load();
  }, [companyId, load]);

  const addStorefront = async () => {
    if (saving) return;
    const invalidDomain = domainMessage(addForm.domain);
    if (invalidDomain) {
      setAddDomainError(invalidDomain);
      return;
    }
    const requested = companyId;
    setSaving(true);
    setAddError(null);
    try {
      const { storefront } = await post<{ storefront: StorefrontSummary }>(
        `/api/companies/${requested}/storefronts`,
        { domain: addForm.domain.trim(), name: addForm.name.trim() },
      );
      // The row belongs to the company that was shown when the POST left; a switch drops it.
      if (companyIdRef.current !== requested) return;
      setRows((current) => sortRows([storefront, ...(current ?? [])]));
      setOpenId(storefront.storefront_id);
      setAdding(false);
      setAddForm({ domain: "", name: "" });
      onSaved?.("Storefront added");
    } catch (err: any) {
      if (companyIdRef.current !== requested) return;
      // The API's own reason wins when it names one; the generic "Validation failed" is replaced
      // by the field's wording. A 409 is always the domain: another storefront holds it.
      const specific = err?.message && err.message !== "Validation failed" ? err.message : null;
      if (err?.status === 400 || err?.status === 409) setAddDomainError(specific ?? DOMAIN_MESSAGE);
      else setAddError(err?.message || "Failed to add the storefront");
    } finally {
      setSaving(false);
    }
  };

  // Two writes on one row can answer out of order; a response older than the row shown is stale.
  const replaceRow = (next: StorefrontSummary) => {
    setRows((current) => (current ?? []).map((row) =>
      row.storefront_id === next.storefront_id && row.updated_at <= next.updated_at ? next : row,
    ));
  };

  const dropRow = (storefrontId: number) => {
    setRows((current) => (current ?? []).filter((row) => row.storefront_id !== storefrontId));
    setOpenId(null);
  };

  const single = rows?.length === 1;
  // An empty field is not wrong yet, so its message waits until something has been typed.
  const addDomainFault = (addForm.domain.trim() ? domainMessage(addForm.domain) : null) ?? addDomainError;

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h6">Storefronts</Typography>
          <Typography variant="body2" color="text.secondary">
            What the buyer saw. Each dispute prints the storefront its order was placed on.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => { setAdding(true); setAddError(null); setAddDomainError(null); }} sx={{ flexShrink: 0 }}>
          Add storefront
        </Button>
      </Stack>

      {adding && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label="Domain"
              placeholder="example.com"
              autoFocus
              value={addForm.domain}
              onChange={(event) => {
                setAddForm((current) => ({ ...current, domain: event.target.value }));
                setAddDomainError(null);
              }}
              size="small"
              error={addDomainFault !== null}
              helperText={addDomainFault ?? undefined}
            />
            <TextField
              label="Name"
              value={addForm.name}
              onChange={(event) => setAddForm((current) => ({ ...current, name: event.target.value }))}
              size="small"
            />
            <Button
              variant="contained"
              onClick={addStorefront}
              disabled={saving || domainMessage(addForm.domain) !== null || !addForm.name.trim()}
            >
              Add
            </Button>
            <Button onClick={() => { setAdding(false); setAddError(null); setAddDomainError(null); }} disabled={saving}>Cancel</Button>
          </Stack>
          {addError && <FormHelperText error>{addError}</FormHelperText>}
        </Box>
      )}

      {loadError && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={<Button color="inherit" size="small" onClick={() => { setRows(null); load(); }}>Retry</Button>}
        >
          {loadError}
        </Alert>
      )}

      {rows === null ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {[0, 1, 2].map((index) => <Skeleton key={index} variant="rectangular" height={36} />)}
        </Stack>
      ) : rows.length === 0 ? (
        !loadError && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            No storefronts yet. One appears on its own the first time a dispute's order names a domain, or add one now.
          </Typography>
        )
      ) : (
        <Table size="small" sx={{ mt: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Logo</TableCell>
              <TableCell>Checkout</TableCell>
              <TableCell>Policies</TableCell>
              <TableCell>Support</TableCell>
              <TableCell>Origin</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const open = openId === row.storefront_id;
              return (
                <Fragment key={row.storefront_id}>
                  <TableRow>
                    <TableCell>
                      {row.domain
                        ? <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.domain}</Typography>
                        : <Typography variant="body2" color="text.secondary">No domain yet</Typography>}
                      {single && (
                        <Typography variant="caption" color="text.secondary">
                          Funnel orders with no sales URL use this storefront
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <StatusDot done={row.completeness.logo} label={row.completeness.logo ? "Logo uploaded" : "Logo missing"} />
                    </TableCell>
                    <TableCell>
                      <StatusDot
                        done={row.completeness.checkout}
                        label={row.completeness.checkout ? "Checkout screenshot uploaded" : "Checkout screenshot missing"}
                      />
                    </TableCell>
                    <TableCell>{`${row.completeness.policies} of ${requiredPolicySlots(row).length}`}</TableCell>
                    <TableCell>
                      <StatusDot
                        done={row.completeness.contactEmail}
                        label={row.completeness.contactEmail ? "Support email set" : "Support email missing"}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip size="small" variant="outlined" label={originLabel(row)} />
                        {needsSetup(row) && <Chip size="small" color="warning" label="Needs setup" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        aria-label={`${open ? "Close" : "Edit"} ${row.name}`}
                        onClick={() => setOpenId(open ? null : row.storefront_id)}
                      >
                        {open ? "Close" : "Edit"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <StorefrontEditor
                          storefront={row}
                          onChange={replaceRow}
                          onDeleted={() => dropRow(row.storefront_id)}
                          onSeeded={(storefrontId) => { setOpenId(storefrontId); load(); }}
                          onSaved={onSaved}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
